// Dashboard del HCE (Rediseño mayor 2026-08-31, Módulo G; rediseño visual
// 2026-09-01 a partir de una captura de referencia del usuario) — vista de
// entrada tras el login de Personal Médico. Dos fuentes de datos en
// paralelo: el calendario navegable (día/semana/mes/lista, cualquier fecha,
// GET /api/hce/citas + /citas-kpi) y el resumen fijo de HOY (pacientesHoy/
// alertas clínicas/estado por cita, GET /api/hce/dashboard-hoy) — la
// columna derecha SIEMPRE muestra hoy, sin importar qué esté navegando el
// calendario de la izquierda.
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { crearUltimaPeticion } from '@/lib/ultimaPeticion';
import { useEventosSala } from '@/lib/useEventosSala';
import {
  obtenerCitasDashboard,
  obtenerKpisDashboard,
  obtenerResumenHoy,
  type CitaDashboard,
  type CitasDashboard,
  type KpisDashboard,
  type RangoDashboard,
  type ResumenHoyDashboard,
} from '@/lib/hceVault';
import { AccesosRapidos } from './AccesosRapidos';
import { AlertasClinicas } from './AlertasClinicas';
import { CalendarioCitas } from './CalendarioCitas';
import { IndicadorEnVivo } from './IndicadorEnVivo';
import { KpiTile } from './KpiTile';
import { PanelAlertasAuditoria } from './PanelAlertasAuditoria';
import { AvisosCierreSala } from './AvisosCierreSala';
import { SalaPanel } from './SalaPanel';
import { TareasPendientes } from './TareasPendientes';
import { TopNav } from './TopNav';

type Vista = RangoDashboard | 'lista';

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vista, setVista] = useState<Vista>('dia');
  const [fecha, setFecha] = useState(() => new Date());
  const [citas, setCitas] = useState<CitasDashboard | null>(null);
  const [kpis, setKpis] = useState<KpisDashboard | null>(null);
  const [resumenHoy, setResumenHoy] = useState<ResumenHoyDashboard | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rangoParaBackend: RangoDashboard = vista === 'lista' ? 'semana' : vista;
  // Solo la recarga más reciente escribe el estado: una respuesta vieja (otro rango/fecha, o dos silenciosas en vuelo) no pisa a la nueva.
  const ultima = useRef(crearUltimaPeticion());

  // `silencioso`: recarga disparada por el canal en vivo o por el respaldo periódico; no parpadea el «Cargando…» ni borra un error a la vista.
  const cargar = useCallback(async (silencioso = false) => {
    const peticion = ultima.current.nueva();
    if (!silencioso) {
      setCargando(true);
      setError(null);
    }
    try {
      const [c, k, h] = await Promise.all([
        obtenerCitasDashboard(rangoParaBackend, fecha),
        // Los 3 KPIs de esta fila (citas ingresadas/nuevos/recurrentes) son
        // siempre de HOY, igual que pacientesHoy/alertas — no siguen la
        // navegación del calendario de abajo.
        obtenerKpisDashboard('dia', new Date()),
        obtenerResumenHoy(),
      ]);
      if (!peticion.vigente()) return;
      setCitas(c);
      setKpis(k);
      setResumenHoy(h);
    } catch {
      if (!silencioso && peticion.vigente()) setError(t('dashboard.errorCargar'));
    } finally {
      if (peticion.vigente()) setCargando(false);
    }
  }, [rangoParaBackend, fecha, t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Canal en vivo (Sala CRM<->HCE, rebanada 3): el calendario y el estado por cita cambian con la Sala y con las citas.
  const { estado: enVivo } = useEventosSala(() => void cargar(true));

  function ingresarPaciente(cita: CitaDashboard): void {
    if (!cita.medplumPatientId) return;
    void navigate(`/${cita.medplumPatientId}`);
  }

  function unirseVideollamada(cita: CitaDashboard): void {
    if (!cita.medplumPatientId) return;
    void navigate(`/telemedicina/${cita.citaId}/${cita.medplumPatientId}`);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 py-4">
      <TopNav activo="dashboard" />
      <IndicadorEnVivo estado={enVivo} />

      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {citas?.sinEspecialidadVinculada && (
        <Alert>
          <AlertDescription>{t('dashboard.sinVinculo')}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <CalendarioCitas
            vista={vista}
            onVistaChange={setVista}
            fecha={fecha}
            onFechaChange={setFecha}
            citas={citas?.citas ?? []}
            cargando={cargando}
            onIngresarPaciente={ingresarPaciente}
            onUnirseVideollamada={unirseVideollamada}
            visiblesDia={3}
          />
          {/* Avisos de la Sala para el médico (rebanada 5c): consultas que el cierre de medianoche dejó por revisar. Solo se dibuja con avisos. */}
          <AvisosCierreSala />
          {/* Sala del médico (Sala CRM↔HCE, rebanada 5): el MISMO componente de la página «Sala», debajo del calendario. */}
          <SalaPanel variante="dashboard" />
          <PanelAlertasAuditoria />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <KpiTile label={t('dashboard.kpi.pacientesHoy')} valor={resumenHoy?.pacientesHoy} />
            <KpiTile label={t('dashboard.kpi.alertas')} valor={resumenHoy?.alertas.length} />
            <KpiTile label={t('dashboard.kpi.total')} valor={kpis?.totalCitas} />
            <KpiTile label={t('dashboard.kpi.nuevos')} valor={kpis?.pacientesNuevos} />
            <div className="col-span-2">
              <KpiTile label={t('dashboard.kpi.recurrentes')} valor={kpis?.pacientesRecurrentes} />
            </div>
          </div>

          <AlertasClinicas alertas={resumenHoy?.alertas ?? []} />
          <AccesosRapidos />
          <TareasPendientes />
          {/* BA-C-10: el respaldo completo del consultorio ya no está en el tablero del personal médico — el servidor lo entrega solo al administrador del HCE (panel de Usuarios). */}
        </div>
      </div>
    </div>
  );
}
